/* eslint-disable @typescript-eslint/no-explicit-any */

import { TFunction } from "i18next";

import { CircularProgress } from "@mui/material";

import { SvgIcon } from "@/components/Icon/SvgIcon";
import { TableColumn } from "@/components/Table/Table";
import { ListSourceType } from "@/enum/list.enum";

import { ReactComponent as eye } from "@assets/icons/create-list/eye.svg";
import { ReactComponent as pencil } from "@assets/icons/create-list/pencil.svg";
import { ReactComponent as trash } from "@assets/icons/create-list/trash.svg";
import { ReactComponent as copy } from "@assets/icons/create-list/copy.svg";
import { ReactComponent as downloadIcon } from "@assets/icons/download.svg";

import { getCsvEnrichmentSourceColumns } from "./csvEnrichmentSourceColumns";
import { getLiProfileSourceColumns } from "./liProfileSourceColumns";
import { getParallelAiSourceColumns } from "./parallelAiSourceColumns";

export const getCustomTableColumns = ({
	type,
	downloadIsLoading,
	handleDownload,
	handleTrackerDownload,
	handleSelect,
	handleDuplicate,
	handleDeleteModal,
	handleTrigger,
	handlePreviewModal,
	handleStatistics,
	t,
}: {
	type: ListSourceType;
	downloadIsLoading?: number | null;
	handleDownload?: (row: any) => void;
	handleTrackerDownload?: (row: any) => void;
	handleSelect?: (row: any) => void;
	handleDuplicate?: (row: any) => void;
	handleDeleteModal?: (row: any) => void;
	handleTrigger?: (row: any) => void;
	handlePreviewModal?: (row: any) => Promise<void>;
	handleStatistics?: (row: any) => void;
	t: TFunction<"translation">;
}) => {
	const liCompanyActivityColumns: TableColumn[] = [
		{
			title: "name",
			uid: "name",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => <div className="max-w-[250px] truncate">{row.name}</div>,
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center grow justify-end">
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={copy}
						onClick={() => {
							handleDuplicate?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={pencil}
						onClick={() => {
							handleSelect?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];

	const generalColumns: TableColumn[] = [
		{
			title: "name",
			uid: "name",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => <div className="max-w-[250px] truncate">{row.name}</div>,
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center grow justify-end">
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={copy}
						onClick={() => {
							handleDuplicate?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={pencil}
						onClick={() => {
							handleSelect?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];

	const liPostColumns: TableColumn[] = [
		{
			title: "type",
			uid: "type",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => <div className="grow-1 truncate">{row.type}</div>,
		},
		{
			title: "value",
			uid: "value",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="grow-0 max-w-[100px] md:max-w-[100px] lg:max-w-[150px] xl:max-w-[200px] truncate">
					{row.query || row.postUrl || row.fromMember?.title || row.fromOrganization?.title}
				</div>
			),
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center justify-end grow-1">
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={copy}
						onClick={() => {
							handleDuplicate?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={pencil}
						onClick={() => {
							handleSelect?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];

	const liProfileVisitsColumns: TableColumn[] = [
		{
			title: "name",
			uid: "name",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => <div className="grow truncate">{row.name}</div>,
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center justify-end grow">
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={pencil}
						onClick={() => {
							handleSelect?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];

	const mapColumns: TableColumn[] = [
		{
			title: "name",
			uid: "name",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => <div className="flex items-center">{row.name}</div>,
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center justify-end grow">
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={eye}
						onClick={() => {
							handlePreviewModal?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={copy}
						onClick={() => {
							handleDuplicate?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={pencil}
						onClick={() => {
							handleSelect?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];

	const liEventColumns: TableColumn[] = [
		{
			title: "name",
			uid: "name",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => <div className="flex items-center">{row.name}</div>,
		},
		{
			title: "type",
			uid: "type",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => <div className="flex items-center">{row.type}</div>,
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center grow justify-end">
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={copy}
						onClick={() => {
							handleDuplicate?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={pencil}
						onClick={() => {
							handleSelect?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];

	const jobColumns: TableColumn[] = [
		{
			title: "query",
			uid: "query",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,
			render: (row: any) => <div className="flex items-center">{row.query}</div>,
		},
		{
			title: "location",
			uid: "location",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,
			render: (row: any) => <div className="flex items-center">{row.location?.title}</div>,
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center grow justify-end">
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={copy}
						onClick={() => {
							handleDuplicate?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={pencil}
						onClick={() => {
							handleSelect?.(row);
						}}
					/>
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];

	const liProfileColumns: TableColumn[] = getLiProfileSourceColumns({
		t,
		handleSelect,
		handleDuplicate,
		handleDeleteModal,
	});

	const csvEnrichmentColumns: TableColumn[] = getCsvEnrichmentSourceColumns({
		t,
		downloadIsLoading,
		handleDownload,
		handleDeleteModal,
		handleStatistics,
	});

	if (type === ListSourceType.LI_POST) {
		return liPostColumns;
	}

	if (type === ListSourceType.LI_PROFILE_VISITS) {
		return liProfileVisitsColumns;
	}

	if (type === ListSourceType.LI_COMPANY_ACTIVITY) {
		return liCompanyActivityColumns;
	}

	if (type === ListSourceType.MAPS) {
		return mapColumns;
	}

	if (type === ListSourceType.LI_EVENTS) {
		return liEventColumns;
	}

	if (type === ListSourceType.JOBS) {
		return jobColumns;
	}

	if (type === ListSourceType.CSV_ENRICHMENTS) {
		return csvEnrichmentColumns;
	}

	if (type === ListSourceType.TRACKER) {
		const trackerFileColumns: TableColumn[] = [
			{
				title: "name",
				uid: "name",
				allowExclude: false,
				hideable: false,
				moveable: false,
				show: true,

				render: (row: any) => <div className="flex items-center">{row.name}</div>,
			},
			{
				title: "",
				uid: "action",
				allowExclude: false,
				hideable: false,
				moveable: false,
				show: true,

				render: (row: any) => (
					<div className="flex items-center grow justify-end">
						{(!downloadIsLoading || downloadIsLoading !== row.id) && (
							<SvgIcon
								className="min-w-[20px] h-[20px] cursor-pointer text-gray-700 ml-1"
								svgIcon={downloadIcon}
								onClick={() => {
									!downloadIsLoading && handleTrackerDownload?.(row);
								}}
							/>
						)}
						{downloadIsLoading && downloadIsLoading === row.id && (
							<div className=" w-[20px] h-[20px] flex items-center justify-center ml-1">
								<CircularProgress size={20} />
							</div>
						)}
						<SvgIcon
							className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
							svgIcon={trash}
							onClick={() => {
								handleDeleteModal?.(row);
							}}
						/>
					</div>
				),
			},
		];

		return trackerFileColumns;
	}

	if (type === ListSourceType.LI_PROFILE) {
		return liProfileColumns;
	}

	if (type === ListSourceType.LI_PEOPLE) {
		return liEventColumns;
	}

	// LI_SN uses the same name + actions layout as the generic columns.
	if (type === ListSourceType.LI_SN) {
		return generalColumns;
	}

	if (type === ListSourceType.PARALLEL_AI) {
		return getParallelAiSourceColumns({
			t,
			handleSelect,
			handleDuplicate,
			handleDeleteModal,
			handleTrigger,
		});
	}

	return generalColumns;
};
